import { ArrowLeftOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { Button, Col, Form, message, Modal, notification, Row, Space, Spin, Typography } from 'antd';
import { Loader } from 'components/common/Loader';
import { PostedLineItemsModal } from 'components/common/PostedLineItemsModal';
import WimsicalError from 'components/common/WimsicalError/WimsicalError';
import { ActionControls, StepControls } from 'components/Create/common';
import { DeploymentRequestInfo } from 'components/Create/common/DeploymentRequestInfo';
import { SparingRequestConfirmation } from 'components/Create/common/SparingtRequestConfirmation.tsx';
import { FormikProvider, useFormik } from 'formik';
import { formatErrorMessage } from 'helpers/formatErrorMessage';
import { QueryErrorModel } from 'models/ErrorModel';
import { DefectiveReturnItemPayload, SparingRequestPayload } from 'models/SparingRequests';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useGetSparingRequestConfigurationTypesQuery } from 'redux/services/calvin/sparingRequestConfigTypes';
import {
  useAddUserToSmsNotificationsMutation,
  useCreateAttachmentMutation,
  useGetSparingRequestQuery,
  usePostSparingRequestMutation,
  useRemoveUserFromSmsNotificationsMutation,
  useUpdateSparingRequestMutation
} from 'redux/services/twittlebugs/sparingRequestsApi';
import { resetAttachments } from 'redux/slices/attachmentsSlice';
import { setFullOutageAddress, setFullShippingAddress, setIsExit, setIsPost } from 'redux/slices/formSlice';
import { useAppSelector } from 'redux/store';
import { getDefectiveReturnsRequiredInfoErrors, isDefectiveReturnsInformationRequirementActive, renumberDefectiveReturnItems } from 'utils/defectiveReturns';
import { mapSlaTypeForBackend } from 'utils/slaMapping';

export const EditDeploymentRequestLayout = (): JSX.Element => {
  /* ******************** Hooks ******************** */
  const { id } = useParams();
  const { data, isLoading, isError, isFetching, error } = useGetSparingRequestQuery(id ?? skipToken);
  const nav = useNavigate();
  const { attachments } = useAppSelector((state) => state.attachments);
  const [createAttachment] = useCreateAttachmentMutation();
  const { user, postRequestLoadingMessage } = useAppSelector((state) => state.app);
  const { isExit, isPost } = useAppSelector((state) => state.form);
  const dispatch = useDispatch();
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(undefined);

  const [editSparing, { data: updateResponse, isSuccess: isUpdateSuccess }] = useUpdateSparingRequestMutation();
  const [postSparingRequest, { data: postedData, isSuccess }] = usePostSparingRequestMutation();

  const [addUserToSms] = useAddUserToSmsNotificationsMutation();
  const [removeUserFromSms] = useRemoveUserFromSmsNotificationsMutation();

  const userNumber = user?.['https://acuity.mdsiinc.com/user/user_metadata']?.phone_number;

  const isUserOptedIn = data?.notificationSms?.some((sparingUser) => sparingUser.userId === user?.sub) ?? false;

  const { data: requestTypesData } = useGetSparingRequestConfigurationTypesQuery();

  const formik = useFormik<SparingRequestPayload>({
    initialValues: {
      requestNumber: data?.requestNumber?.split('-')[1] ?? '',
      customerReferenceNumber: data?.customerReferenceNumber ?? '',
      customerPurchaseOrderNumber: data?.customerPurchaseOrderNumber ?? '',
      endCustomer: data?.endCustomer ?? '',
      sparingRequestTypeId: data?.sparingRequestTypeId ?? '',
      shippingLocationId: data?.shippingLocation?.id ?? '',
      lineItems:
        data?.lineItems?.map((item, idx) => ({
          productNumber: item.productNumber,
          lineNumber: idx + 1,
          quantity: item.quantity,
          subLines: item.subLines?.map((subLine) => ({
            ...subLine,
            serviceLevelAgreement: mapSlaTypeForBackend(subLine.serviceLevelAgreement)
          }))
        })) ?? [],
      outageLocationId: data?.outageLocation?.id ?? '',
      contactEmail: data?.contact?.email ?? '',
      contactName: data?.contact?.name ?? '',
      contactPhoneNumber: data?.contact?.phoneNumber ?? '',
      requestedDeliveryDate: '',
      notificationEmails: user && user.email ? [user.email] : [],
      notes: data?.notes ?? '',
      isLabConfigurationRequired: data?.isLabConfigurationRequired ?? false,
      isActive: true,
      isDeleted: false,
      isUserOptedIn,
      defectiveReturns: data?.defectiveReturns
        ? {
            contactName: data?.defectiveReturns.contactName ?? '',
            contactPhoneNumber: data?.defectiveReturns.contactPhoneNumber ?? '',
            contactEmail: data?.defectiveReturns.contactEmail ?? '',
            waybillDeliveryMethods: data?.defectiveReturns.waybillDeliveryMethods,
            items:
              data?.defectiveReturns.items?.map((it) => ({
                lineNumber: it.lineNumber,
                productNumber: it.productNumber,
                serialNumber: it.serialNumber ?? '',
                quantity: it.quantity ?? 1,
                caseNumber: it.caseNumber,
                rmaNumber: it.rmaNumber,
                // The wire format is `used`; "Didn't Use" is the inverse of it, and unchecked by default.
                used: it.used ?? true,
                // Carried so a save sends the stored note back: the API replaces the whole defective
                // returns block on update, and an item sent without notes is stored with an empty one.
                notes: it.notes
              })) ?? []
          }
        : undefined
    },
    validate: (values) => {
      const errors: any = {};
      const selectedType = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);

      if (selectedType) {
        if (selectedType.isReference1Required && !values.customerReferenceNumber?.trim()) {
          errors.customerReferenceNumber = `${selectedType.reference1Label || 'Primary Reference Number'} is required`;
        }

        if (selectedType.isReference2Required && !values.customerPurchaseOrderNumber?.trim()) {
          errors.customerPurchaseOrderNumber = `${selectedType.reference2Label || 'Secondary Reference Number'} is required`;
        }

        if (selectedType.isEndCustomerVisible && selectedType.isEndCustomerVisibleRequired && !values.endCustomer?.trim()) {
          errors.endCustomer = 'End Customer is required';
        }

        // MDSI1-4304: Return Part Number / Return Serial Number on defective returns. Only a Post is
        // blocked - formik refuses to submit at all while validate returns anything, and a draft has
        // to stay saveable, so this error is raised only for the submit that actually posts.
        const defectiveReturnsRequiredInfo = getDefectiveReturnsRequiredInfoErrors({
          items: values.defectiveReturns?.items,
          lineItems: values.lineItems,
          requestType: selectedType
        });

        // message is set exactly when the helper is not satisfied; guarding on it keeps an
        // undefined from ever landing in errors, which formik would treat as a silent block.
        if (isPost && defectiveReturnsRequiredInfo.message) {
          errors.defectiveReturns = defectiveReturnsRequiredInfo.message;
        }
      }

      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      // Validate required fields based on sparing request type configuration
      const selectedType = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);

      if (selectedType) {
        const missingFields: string[] = [];

        if (selectedType.isReference1Required && !values.customerReferenceNumber?.trim()) {
          missingFields.push(selectedType.reference1Label || 'Primary Reference Number');
          setFieldError('customerReferenceNumber', `${selectedType.reference1Label || 'Primary Reference Number'} is required`);
        }

        if (selectedType.isReference2Required && !values.customerPurchaseOrderNumber?.trim()) {
          missingFields.push(selectedType.reference2Label || 'Secondary Reference Number');
          setFieldError('customerPurchaseOrderNumber', `${selectedType.reference2Label || 'Secondary Reference Number'} is required`);
        }

        if (selectedType.isEndCustomerVisible && selectedType.isEndCustomerVisibleRequired && !values.endCustomer?.trim()) {
          missingFields.push('End Customer');
          setFieldError('endCustomer', 'End Customer is required');
        }

        // MDSI1-4304: Return Part Number / Return Serial Number on defective returns, Post only.
        const defectiveReturnsRequiredInfo = getDefectiveReturnsRequiredInfoErrors({
          items: values.defectiveReturns?.items,
          lineItems: values.lineItems,
          requestType: selectedType
        });

        if (isPost && defectiveReturnsRequiredInfo.missingFieldsSummary && defectiveReturnsRequiredInfo.message) {
          missingFields.push(defectiveReturnsRequiredInfo.missingFieldsSummary);
          setFieldError('defectiveReturns', defectiveReturnsRequiredInfo.message);
        }

        if (missingFields.length > 0) {
          notification.error({
            message: 'Required Fields Missing',
            description: `Please fill in the following required fields: ${missingFields.join(', ')}`,
            className: 'custom-class',
            style: {
              width: 600
            },
            duration: 5
          });

          setSubmitting(false);

          // This submit attempt is abandoned - a later Save must not be mistaken for a Post.
          dispatch(setIsPost(false));
          dispatch(setIsExit(false));

          return;
        }
      }

      setLoadingMessage('Updating sparing request...');
      // isActivateDefectiveReturns still exists on the sparing request type, and Create/Layout.tsx
      // gates the defective returns block on it. This screen deliberately does not: an existing
      // request may already carry defective returns saved while the flag was on, and dropping the
      // block on every edit would delete them. Whether an edit should follow the flag is an open
      // question on MDSI1-4304 - until it is answered, an edit always sends what it has.
      const shouldIncludeDefectiveReturns = true;

      const userDefective = values.defectiveReturns;

      // MDSI1-4304: only while the requirement is active are the items matched to line items by
      // product and renumbered. With it off the items go out exactly as on main - line numbers
      // passed through untouched - apart from reading `used` instead of the non-existent `didntUse`.
      const isDefectiveReturnsInfoRequired = isDefectiveReturnsInformationRequirementActive(requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId));

      const body: SparingRequestPayload = {
        ...values,
        requestNumber: `${data?.requestNumber.split('-')[0]}${values.requestNumber && `-${values.requestNumber}`}`,
        lineItems: values.lineItems.map((item, idx) => ({
          productNumber: item.productNumber,
          lineNumber: idx + 1,
          quantity: item.quantity,
          subLines: item.subLines?.map((subLine) => ({
            ...subLine,
            serviceLevelAgreement: mapSlaTypeForBackend(subLine.serviceLevelAgreement)
          }))
        })),
        defectiveReturns: shouldIncludeDefectiveReturns
          ? {
              contactName: userDefective?.contactName ?? values.contactName ?? '',
              contactPhoneNumber: userDefective?.contactPhoneNumber ?? values.contactPhoneNumber ?? '',
              contactEmail: userDefective?.contactEmail ?? values.contactEmail ?? '',
              waybillDeliveryMethods: userDefective?.waybillDeliveryMethods,
              items: isDefectiveReturnsInfoRequired
                ? renumberDefectiveReturnItems(userDefective?.items, values.lineItems)
                : userDefective?.items && userDefective.items.length
                ? userDefective.items.map((it) => ({
                    lineNumber: it.lineNumber,
                    productNumber: it.productNumber,
                    serialNumber: it.serialNumber ?? '',
                    quantity: it.quantity ?? 1,
                    caseNumber: it.caseNumber,
                    rmaNumber: it.rmaNumber,
                    used: it.used ?? true,
                    notes: it.notes
                  }))
                : values.lineItems.flatMap((item, idx) => {
                    const quantity = item.quantity;
                    const items: DefectiveReturnItemPayload[] = [];

                    for (let i = 0; i < quantity; i++) {
                      items.push({
                        lineNumber: idx + 1,
                        productNumber: item.productNumber,
                        serialNumber: '',
                        caseNumber: '',
                        rmaNumber: '',
                        quantity: 1,
                        used: true // Default to true since "Didn't Use" checkbox is unchecked by default
                      });
                    }

                    return items;
                  })
            }
          : undefined
      };

      if (!id) return message.error('An error occured, the team has been notified.');

      try {
        const response = await editSparing({ id, body }).unwrap();

        if (isUserOptedIn && !values.isUserOptedIn) {
          await removeUserFromSms(id).unwrap();
        }

        if (!isUserOptedIn && values.isUserOptedIn) {
          await addUserToSms({ id, payload: {} }).unwrap();
        }

        if (attachments.length) {
          setLoadingMessage('Updating attachments...');

          for await (const attachment of attachments) {
            const formData = new FormData();

            formData.append(`${attachment.name}`, attachment);
            await createAttachment({ file: formData, id }).unwrap();
          }
        }
        if (!isExit && !isPost && response) {
          notification.success({
            message: 'Success',
            description: 'Sparing request updated successfully.',
            className: 'custom-class',
            style: {
              width: 600
            },
            duration: 10
          });
        }

        if (isPost) {
          setLoadingMessage(undefined);
          await postSparingRequest({ sparingResponse: response, sparingRequestId: id }).unwrap();

          dispatch(setIsPost(false));
        }
        dispatch(resetAttachments());
      } catch (e) {
        setLoadingMessage(undefined);
        console.error(e, 'error');

        const error = e as QueryErrorModel;
        const errorMessage = error?.data?.errorMessage ? error?.data?.errorMessage : error.data ? (error?.data as string) : 'An error occured, the team has been notified.';

        dispatch(setIsPost(false));
        dispatch(setIsExit(false));

        notification.error({
          message: 'Error',
          description: errorMessage,
          className: 'custom-class',
          style: {
            width: 600
          },
          duration: 10
        });
      }
    }
  });

  const handleClose = (): void => {
    Modal.destroyAll();
    if (isExit) nav('/');
    else nav(`/edit/${updateResponse?.id}`);
    dispatch(setIsExit(false));
  };

  useEffect(() => {
    if (data?.outageLocation) dispatch(setFullOutageAddress(data?.outageLocation));
    if (data?.shippingLocation) dispatch(setFullShippingAddress(data?.shippingLocation));
  }, [data?.outageLocation, data?.shippingLocation, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setFullOutageAddress(null));
      dispatch(setFullShippingAddress(null));
      dispatch(resetAttachments());
      // Leaving the form abandons any submit attempt in flight - never carry a Post into the next one.
      dispatch(setIsPost(false));
      dispatch(setIsExit(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <Loader loadingMessage="Loading sparing request..." />;

  if (isError) {
    const err = error as QueryErrorModel;

    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Row justify={'space-between'} style={{ width: '100%' }}>
          <Col>
            <Typography.Title level={4}>Edit Sparing Request</Typography.Title>
          </Col>
          <Col>
            <Space>
              <Button onClick={(): void => nav('/search')} icon={<ArrowLeftOutlined />}>
                Back to Search
              </Button>
            </Space>
          </Col>
        </Row>
        <WimsicalError title={formatErrorMessage(typeof err.status === 'number' ? err.status : 400)} subTitle={err?.data?.errorMessage ? err.data.errorMessage : ''} statusCode={err.status} />
      </Space>
    );
  }

  return (
    <>
      <FormikProvider value={formik}>
        <Spin spinning={formik.isSubmitting || !!postRequestLoadingMessage || isFetching} indicator={<Loader loadingMessage={postRequestLoadingMessage ?? loadingMessage} />}>
          <Form layout="vertical">
            <ActionControls />
            <DeploymentRequestInfo />
            <StepControls />
            <Outlet />
          </Form>
        </Spin>
      </FormikProvider>
      <Modal
        onOk={handleClose}
        open={isUpdateSuccess && !isPost && isExit}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={handleClose}
        closeIcon={undefined}
        bodyStyle={{ background: 'linear-gradient(to right bottom, #979797, #acacac, #c1c1c1, #d7d7d7, #ededed)', padding: 0, height: 400, position: 'relative', borderRadius: 5 }}>
        <SparingRequestConfirmation confirmationInfo={updateResponse} />
      </Modal>
      <PostedLineItemsModal open={isSuccess} submittedSparingRequest={postedData?.data} />
    </>
  );
};
